import {
  useEffect,
  useRef,
  // useState,
  useMemo,
} from 'react';
import { useLocation, NavLink, Outlet } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  IconExpand,
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  ButtonSecondary,
  ButtonSize,
  // InputText,
  PermissionEnum,
  // IconSearch,
  useFilters,
  formatDate,
  useGetList,
  PermissionDenied,
  IconSelected,
  IconBlock,
  SchemaFilterType,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { selectRule } from './store/slices/list';
import {
  transactionMonitoringRulesColumns,
  transactionMonitoringRulesSchema,
  transactionMonitoringRulesColumnsLabels,
  transactionMonitoringRulesFiltersSchema,
} from './variables/list';
import { TransactionMonitoringRule } from '../types';
import {
  useGetTransactionMonitoringRulesValidatorsQuery,
  useLazyGetTransactionMonitoringRulesQuery,
  useBatchToggleTransationMonitoringRulesActiveMutation,
} from './store/services';
import { useGetAmlTransactionTypesQuery } from '../../store/dictionaries/services';

import ROUTES from '../../routes';

import cn from './transaction-monitoring-rules.module.css';

const formatTransationMonitoringRuleList = (
  list: TransactionMonitoringRule[],
): TransactionMonitoringRule[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      createdAt: (
        <div className={!result.active ? cn.ruleRowDisabled : ''}>
          {formatDate(item.createdAt)}
        </div>
      ),
      active: (
        <div
          className={`${!result.active ? cn.ruleRowDisabled : ''} ${
            cn.ruleStatus
          }`}
        >
          {result.active ? (
            <IconSelected
              className={`${cn.ruleStatusIcon} ${cn.ruleActiveIcon}`}
            />
          ) : (
            <IconBlock
              className={`${cn.ruleStatusIcon} ${cn.ruleDisabledIcon}`}
            />
          )}
          {result.active ? 'Active' : 'Inactive'}
        </div>
      ),
      name: (
        <div className={!result.active ? cn.ruleRowDisabled : ''}>
          {result.name}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const TransactionMonitoringRulesList = () => {
  // const [searchValue, setSearchValue] = useState('');

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    transactionMonitoringRules: {
      list: { values, selectedRules, pagination },
    },
    amlDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const [updateStatus, { isLoading: updateInProgress }] =
    useBatchToggleTransationMonitoringRulesActiveMutation();

  useGetTransactionMonitoringRulesValidatorsQuery();
  useGetAmlTransactionTypesQuery();

  const [getRules, { isLoading, isFetching }] =
    useLazyGetTransactionMonitoringRulesQuery();

  const { sort, onSortChange, onFilterChange, filters } = useQueryState(
    transactionMonitoringRulesSchema,
  );

  const { onSort, setPage, onFilter, errorList } = useGetList({
    getList: getRules,
    onFilterChange,
    onSortChange,
    schema: transactionMonitoringRulesSchema,
  });

  const formattedData = useMemo(() => {
    const data = formatTransationMonitoringRuleList(
      values as TransactionMonitoringRule[],
    );

    return data;
  }, [values, sort]);

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: transactionMonitoringRulesFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const onUpdateStatus = () => {
    const activateRules: string[] = [];
    const deactiveRules: string[] = [];

    if (selectedRules.length) {
      values?.forEach((item) => {
        const currentRule = selectedRules.find((id) => item.id === id);

        if (currentRule) {
          if (item.active) {
            deactiveRules.push(item.id);
          } else {
            activateRules.push(item.id);
          }
        }
      });

      if (activateRules.length > 0) {
        updateStatus({
          active: true,
          ids: activateRules,
        });
      }

      if (deactiveRules.length > 0) {
        updateStatus({
          active: false,
          ids: deactiveRules,
        });
      }
    }
  };

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(selectedRules, rows)) {
      dispatch(selectRule(rows));
    }
  };

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      !values &&
      sort &&
      Object.keys(sort).length &&
      activePermissions?.includes(PermissionEnum.AML_MONITORING_RULES_LIST)
    ) {
      onFilter({
        ...filters,
        active: {
          value: ['true'],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [values, sort, activePermissions]);

  useEffect(() => {
    let title = 'Transaction monitoring rules';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;
  let globalContent = (
    <div className={cn.rulesLoader}>
      <IconLoader />
    </div>
  );

  let statusButtonText;

  if (updateInProgress) {
    statusButtonText = 'Loading...';
  } else {
    statusButtonText = `Deactivate/Activate ${selectedRules.length} rule${
      selectedRules.length > 1 ? 's' : ''
    }`;
  }

  if (activePermissions) {
    if (activePermissions.includes(PermissionEnum.AML_MONITORING_RULES_LIST)) {
      if (isLoading) {
        content = (
          <div className={cn.rulesLoader}>
            <IconLoader />
          </div>
        );
      } else {
        content = (
          <Table
            columns={transactionMonitoringRulesColumns}
            data={formattedData || []}
            onSort={(name) => onSort(name)}
            filters={filters}
            sort={sort}
            columnsLabels={transactionMonitoringRulesColumnsLabels}
            onFilterChange={(data) => onFilter(data)}
            tableClassName={!formattedData?.length ? cn.emptyTable : ''}
            isLoading={isFetching}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            onRowsSelect={
              activePermissions?.includes(
                PermissionEnum.AML_MONITORING_RULES_EDIT_ACTION,
              ) ||
              activePermissions?.includes(
                PermissionEnum.AML_MONITORING_RULES_DELETE_ACTION,
              )
                ? onRowsSelect
                : undefined
            }
            take="30"
            pagination={pagination}
            setPage={(data) => setPage({ ...data, filters })}
            error={errorList}
            clickable
          />
        );
      }

      globalContent = (
        <div className={cn.rules}>
          <div className={cn.rulesBottom} ref={scrollRef}>
            <div className={cn.rulesTopWrapper}>
              <div className={cn.rulesTitle}>Transaction monitoring rules</div>
              <div className={cn.rulesTop}>
                {activePermissions?.includes(
                  PermissionEnum.AML_MONITORING_RULES_ADD_ACTION,
                ) && (
                  <NavLink
                    to={`${ROUTES.TRANSACTION_MONITORING_RULES_CREATE}${
                      search || ''
                    }`}
                    className={cn.rulesCreateLink}
                  >
                    <div className={cn.rulesCreateLinkContent}>
                      <IconExpand className={cn.rulesCreateLinkIcon} />
                      <span>Add new rule</span>
                    </div>
                  </NavLink>
                )}

                {selectedRules.length > 0 &&
                  activePermissions?.includes(
                    PermissionEnum.AML_MONITORING_RULES_DELETE_ACTION,
                  ) && (
                    <ButtonSecondary
                      className={`button-white ${cn.rulesControlButton}`}
                      size={ButtonSize.SMALL}
                      text={statusButtonText}
                      onClick={() => onUpdateStatus()}
                    />
                  )}
              </div>
              {/* <InputText
                name="searchText"
                placeholder="Search all lists by name..."
                prefix={<IconSearch />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              /> */}
            </div>
            <div className={cn.rulesBottomWrapper}>{content}</div>
            <Outlet />
          </div>
        </div>
      );
    } else {
      globalContent = <PermissionDenied />;
    }
  }

  return globalContent;
};

export default TransactionMonitoringRulesList;
